import React, { useEffect, useRef } from "react";
import { ItineraryService } from "../../services/ItineraryService";
import { getUIColorPalette } from "../../theme/ColorPalette";
import type { SearchResultItem } from "../../types/geocode-ui.types";
import { GeocodeType } from "../../types/geocode-ui.types";

interface MapContextMenuProps {
    "visible":         boolean;
    "x":               number;
    "y":               number;
    "label":           string;
    "loading"?:        boolean;
    "isDarkUi":        boolean;
    "onClose":         () => void;
    "onSetDeparture"?: ( item: SearchResultItem ) => void;
    "onSetArrival"?:   ( item: SearchResultItem ) => void;
    "buildItem":       () => SearchResultItem;
}

const itineraryService = new ItineraryService();

const buildEnrichedItem = async( item: SearchResultItem ): Promise<SearchResultItem> => {
    try {
        const results = await itineraryService.reverseGeocode(
            item.location.coordinates.lat,
            item.location.coordinates.lon,
            GeocodeType.ADDRESS,
        );
        const address = results?.[0]?.result;

        return {
            ...item,
            "resume": address != null && address !== ""
                ? item.result !== "" && item.result !== address
                    ? `${ item.result }, ${ address }`
                    : address
                : item.result,
        };
    } catch {
        return item;
    }
};

export const MapContextMenu: React.FC<MapContextMenuProps> = ( {
    visible,
    x,
    y,
    label,
    loading = false,
    isDarkUi,
    onClose,
    onSetDeparture,
    onSetArrival,
    buildItem,
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const menuRef = useRef<HTMLDivElement | null>( null );

    // Clamp menu inside viewport
    useEffect( () => {
        if ( !visible ) {
            return;
        }
        const el = menuRef.current;
        if ( el == null ) {
            return;
        }
        const rect = el.getBoundingClientRect();
        const margin = 8;
        const maxX = Math.max( margin, window.innerWidth - rect.width - margin );
        const maxY = Math.max( margin, window.innerHeight - rect.height - margin );
        const clampedX = Math.max( margin, Math.min( x, maxX ) );
        const clampedY = Math.max( margin, Math.min( y, maxY ) );
        if ( clampedX !== x || clampedY !== y ) {
            el.style.left = `${ clampedX }px`;
            el.style.top  = `${ clampedY }px`;
        }
    }, [ visible, x, y ] );

    if ( !visible ) {
        return null;
    }

    return (
        <>
            <div
                className="absolute inset-0 z-20"
                onClick={onClose}
                onTouchStart={onClose}
                style={{ "background": "transparent" }}
            />
            <div
                className="absolute z-30 min-w-56 rounded-xl border backdrop-blur-md shadow-xl"
                onMouseLeave={onClose}
                ref={menuRef}
                style={{
                    "top":         y,
                    "left":        x,
                    "borderColor": palette.overlayBorder,
                    "background":  palette.overlayCardBackgroundStrong,
                    "color":       palette.overlayTextPrimary,
                }}
            >
                {( label !== "" || loading ) && (
                    <div
                        className="px-4 py-3 border-b text-sm"
                        style={{
                            "borderColor": palette.overlayBorder,
                            "color":       palette.overlayTextSecondary,
                        }}
                    >
                        {loading ? "Chargement…" : label}
                    </div>
                )}
                <div className="py-1">
                    <button
                        className="w-full text-left px-4 py-2 text-sm"
                        onClick={async() => {
                            const enriched = await buildEnrichedItem( buildItem() );
                            onSetDeparture?.( enriched );
                            onClose();
                        }}
                        onMouseEnter={( e ) => {
 e.currentTarget.style.background = palette.interactiveHoverBackground;
}}
                        onMouseLeave={( e ) => {
 e.currentTarget.style.background = "transparent";
}}
                        style={{ "background": "transparent" }}
                    >
                        Définir comme départ
                    </button>
                    <button
                        className="w-full text-left px-4 py-2 text-sm"
                        onClick={async() => {
                            const enriched = await buildEnrichedItem( buildItem() );
                            onSetArrival?.( enriched );
                            onClose();
                        }}
                        onMouseEnter={( e ) => {
 e.currentTarget.style.background = palette.interactiveHoverBackground;
}}
                        onMouseLeave={( e ) => {
 e.currentTarget.style.background = "transparent";
}}
                        style={{ "background": "transparent" }}
                    >
                        Définir comme arrivée
                    </button>
                </div>
            </div>
        </>
    );
};
